import React, { useEffect, useRef } from "react";

const ParticleAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    class Particles {
      constructor(canvas) {
        this.canvas = canvas;
        this.ctx = this.canvas.getContext("2d");
        this.devicePixelRatio = window.devicePixelRatio || 1;

        // Particle Settings
        this.numParticles = this.calculateParticleCount(); // Dynamic particle count
        this.minRadius = 1;
        this.maxRadius = 3;
        this.minOpacity = 0.1;
        this.maxOpacity = 0.4;
        this.minSpeed = 0.02;
        this.maxSpeed = 0.2;
        this.fps = this.isMobile() ? 20 : 30; // Lower FPS for mobile

        this.particles = [];
        this.animationFrameId = null;

        this.init();
      }

      isMobile() {
        return window.innerWidth <= 768; // Consider devices with width <= 768px as mobile
      }

      calculateParticleCount() {
        const baseCount = this.isMobile() ? 150 : 300; // Fewer particles for mobile
        const screenArea = window.innerWidth * window.innerHeight;
        const factor = Math.min(screenArea / 1920 / 1080, 1); // Scale down for smaller screens
        return Math.floor(baseCount * factor);
      }

      _rand(min, max) {
        return Math.random() * (max - min) + min;
      }

      init() {
        this.resizeCanvas();
        this.createParticles();
        this.startAnimation();
        window.addEventListener("resize", this.resizeCanvas.bind(this));
      }

      resizeCanvas() {
        const maxCanvasSize = this.isMobile() ? 1280 : 1920; // Limit canvas size for mobile
        this.canvasWidth = Math.min(window.innerWidth, maxCanvasSize);
        this.canvasHeight = Math.min(window.innerHeight, maxCanvasSize);

        this.canvas.width = this.canvasWidth * this.devicePixelRatio;
        this.canvas.height = this.canvasHeight * this.devicePixelRatio;

        this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);
      }

      createParticles() {
        this.particles = []; // Clear existing particles
        for (let i = 0; i < this.numParticles; i++) {
          const greyValue = Math.floor(this._rand(150, 255)); // Random grey shades
          this.particles.push({
            radius: this._rand(this.minRadius, this.maxRadius),
            xPos: this._rand(0, this.canvas.width / this.devicePixelRatio),
            yPos: this._rand(0, this.canvas.height / this.devicePixelRatio),
            xVelocity: this._rand(this.minSpeed, this.maxSpeed),
            yVelocity: this._rand(this.minSpeed, this.maxSpeed),
            color: `rgba(${greyValue}, ${greyValue}, ${greyValue}, ${this._rand(
              this.minOpacity,
              this.maxOpacity
            )})`,
          });
        }
      }

      drawParticle(particle) {
        this.ctx.fillStyle = particle.color;
        this.ctx.beginPath();
        this.ctx.arc(particle.xPos, particle.yPos, particle.radius, 0, Math.PI * 2, false);
        this.ctx.fill();
      }

      resetParticle(particle) {
        particle.xPos = this._rand(0, this.canvas.width / this.devicePixelRatio);
        particle.yPos = -particle.radius; // Reset particle to top
      }

      updateParticles() {
        this.particles.forEach((particle) => {
          particle.xPos += particle.xVelocity;
          particle.yPos += particle.yVelocity;

          if (
            particle.xPos > this.canvas.width / this.devicePixelRatio ||
            particle.yPos > this.canvas.height / this.devicePixelRatio
          ) {
            this.resetParticle(particle);
          }
        });
      }

      animate() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.updateParticles();
        this.particles.forEach((particle) => this.drawParticle(particle));
        this.animationFrameId = requestAnimationFrame(() => this.animate());
      }

      startAnimation() {
        if (!this.animationFrameId) {
          this.animate();
        }
      }

      stopAnimation() {
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      }
    }

    const canvas = canvasRef.current;
    const particleAnimation = new Particles(canvas);

    // IntersectionObserver for starting and stopping animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          particleAnimation.startAnimation(); // Start animation if visible
        } else {
          particleAnimation.stopAnimation(); // Stop animation if not visible
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);

    return () => {
      observer.disconnect();
      particleAnimation.stopAnimation();
      window.removeEventListener("resize", () => particleAnimation.resizeCanvas());
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: "block",
        // background: "#222", // Dark background for contrast
        width: "100%",
        height: "100%",
      }}
    ></canvas>
  );
};

export default ParticleAnimation;
